/**
 * Spanish text utilities for autocomplete
 * Provides word suggestions and corrections
 */

export interface SpanishSuggestion {
  text: string;
  translation: string;
  type: 'exact' | 'partial' | 'correction';
  correctedFrom?: string;
}

/**
 * Common Spanish words with English translations
 * This is a starter set - can be expanded significantly
 */
const SPANISH_DICTIONARY: Record<string, string> = {
  // Common greetings
  'hola': 'hello',
  'adiós': 'goodbye',
  'adios': 'goodbye',
  'buenos': 'good',
  'buenas': 'good',
  'días': 'days',
  'dias': 'days',
  'tardes': 'afternoons',
  'noches': 'nights',

  // Common verbs
  'ser': 'to be',
  'estar': 'to be',
  'tener': 'to have',
  'hacer': 'to do/make',
  'ir': 'to go',
  'poder': 'to be able',
  'decir': 'to say',
  'dar': 'to give',
  'saber': 'to know',
  'querer': 'to want',
  'llegar': 'to arrive',
  'pasar': 'to pass',
  'deber': 'to owe/must',
  'poner': 'to put',
  'parecer': 'to seem',
  'quedar': 'to stay/remain',
  'creer': 'to believe',
  'hablar': 'to speak',
  'llevar': 'to carry/wear',
  'dejar': 'to leave/let',
  'seguir': 'to follow',
  'encontrar': 'to find',
  'llamar': 'to call',
  'venir': 'to come',
  'pensar': 'to think',
  'salir': 'to leave',
  'volver': 'to return',
  'tomar': 'to take',
  'conocer': 'to know',
  'vivir': 'to live',
  'sentir': 'to feel',
  'tratar': 'to try',
  'mirar': 'to look',
  'contar': 'to count/tell',
  'empezar': 'to begin',
  'esperar': 'to wait/hope',
  'buscar': 'to search',
  'existir': 'to exist',
  'entrar': 'to enter',
  'trabajar': 'to work',
  'escribir': 'to write',
  'perder': 'to lose',
  'producir': 'to produce',
  'ocurrir': 'to happen',
  'entender': 'to understand',
  'pedir': 'to ask for',
  'recibir': 'to receive',
  'recordar': 'to remember',
  'terminar': 'to finish',
  'permitir': 'to allow',
  'aparecer': 'to appear',
  'conseguir': 'to get/achieve',
  'comenzar': 'to commence',
  'servir': 'to serve',
  'sacar': 'to take out',
  'necesitar': 'to need',
  'mantener': 'to maintain',
  'resultar': 'to result',
  'leer': 'to read',
  'caer': 'to fall',
  'cambiar': 'to change',
  'presentar': 'to present',
  'crear': 'to create',
  'abrir': 'to open',
  'considerar': 'to consider',
  'oír': 'to hear',
  'acabar': 'to finish',
  'suponer': 'to suppose',
  'comprender': 'to understand',

  // Common nouns
  'casa': 'house',
  'año': 'year',
  'vez': 'time',
  'día': 'day',
  'parte': 'part',
  'vida': 'life',
  'tiempo': 'time/weather',
  'hombre': 'man',
  'mujer': 'woman',
  'niño': 'boy/child',
  'niña': 'girl',
  'mundo': 'world',
  'país': 'country',
  'pais': 'country',
  'agua': 'water',
  'ciudad': 'city',
  'problema': 'problem',
  'mano': 'hand',
  'trabajo': 'work',
  'familia': 'family',
  'persona': 'person',
  'grupo': 'group',
  'momento': 'moment',
  'nombre': 'name',
  'amigo': 'friend',
  'amiga': 'friend (f)',
  'padre': 'father',
  'madre': 'mother',
  'hijo': 'son',
  'hija': 'daughter',
  'hermano': 'brother',
  'hermana': 'sister',

  // Common adjectives
  'bueno': 'good',
  'grande': 'big',
  'pequeño': 'small',
  'nuevo': 'new',
  'viejo': 'old',
  'primero': 'first',
  'último': 'last',
  'largo': 'long',
  'propio': 'own',
  'mismo': 'same',
  'tanto': 'so much',
  'poco': 'little',
  'mejor': 'better',
  'peor': 'worse',
  'mayor': 'older/greater',
  'joven': 'young',
  'feliz': 'happy',
  'triste': 'sad',

  // Numbers
  'uno': 'one',
  'dos': 'two',
  'tres': 'three',
  'cuatro': 'four',
  'cinco': 'five',
  'seis': 'six',
  'siete': 'seven',
  'ocho': 'eight',
  'nueve': 'nine',
  'diez': 'ten',

  // Common words
  'sí': 'yes',
  'si': 'if',
  'no': 'no',
  'más': 'more',
  'mas': 'but',
  'todo': 'all',
  'otra': 'other (f)',
  'otro': 'other (m)',
  'también': 'also',
  'tambien': 'also',
  'muy': 'very',
  'aquí': 'here',
  'aqui': 'here',
  'ahora': 'now',
  'bien': 'well',
  'donde': 'where',
  'dónde': 'where',
  'cuando': 'when',
  'cuándo': 'when',
  'cómo': 'how',
  'como': 'how/like',
  'qué': 'what',
  'que': 'that',
  'quién': 'who',
  'quien': 'who',
  'por': 'for/by',
  'para': 'for',
  'con': 'with',
  'sin': 'without',
  'sobre': 'about/on',
  'entre': 'between',
  'hasta': 'until',
  'desde': 'since/from',
};

/**
 * Common accent corrections
 */
const ACCENT_CORRECTIONS: Record<string, string> = {
  'adios': 'adiós',
  'dias': 'días',
  'aqui': 'aquí',
  'tambien': 'también',
  'mas': 'más',
  'pais': 'país',
};

/**
 * Normalize text for matching (remove accents, lowercase)
 */
function normalizeSpanish(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Generate Spanish autocomplete suggestions based on user input
 * @param input - User's partial word input
 * @returns Array of suggestions with translations
 */
export function buildSpanishSuggestions(input: string): SpanishSuggestion[] {
  if (!input || input.trim() === '') return [];

  const trimmed = input.trim().toLowerCase();
  const normalized = normalizeSpanish(trimmed);
  const suggestions: SpanishSuggestion[] = [];
  const seen = new Set<string>();

  // 1. Check for exact match
  if (SPANISH_DICTIONARY[trimmed]) {
    suggestions.push({
      text: trimmed,
      translation: SPANISH_DICTIONARY[trimmed],
      type: 'exact',
    });
    seen.add(trimmed);
  }

  // 2. Check for accent correction
  if (ACCENT_CORRECTIONS[trimmed] && !seen.has(ACCENT_CORRECTIONS[trimmed])) {
    const corrected = ACCENT_CORRECTIONS[trimmed];
    suggestions.push({
      text: corrected,
      translation: SPANISH_DICTIONARY[corrected] || '',
      type: 'correction',
      correctedFrom: trimmed,
    });
    seen.add(corrected);
  }

  // 3. Find partial matches (words that start with the input)
  Object.entries(SPANISH_DICTIONARY).forEach(([word, translation]) => {
    if (seen.has(word)) return;

    // Check if word starts with the input (with or without accents)
    const wordNormalized = normalizeSpanish(word);
    if (wordNormalized.startsWith(normalized) && word !== trimmed) {
      suggestions.push({
        text: word,
        translation,
        type: 'partial',
      });
      seen.add(word);
    }
  });

  // Limit to top 10 suggestions
  return suggestions.slice(0, 10);
}

/**
 * Normalize a Spanish word for translation lookup
 * Removes extra whitespace and normalizes accents
 */
export function normalizeSpanishWord(word: string): string {
  return word.trim().toLowerCase();
}
